import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Chip,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Tooltip,
  Alert,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import VisibilityIcon from '@mui/icons-material/Visibility';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PaymentIcon from '@mui/icons-material/Payment';
import PendingIcon from '@mui/icons-material/Pending';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import TableChartIcon from '@mui/icons-material/TableChart';
import DownloadIcon from '@mui/icons-material/Download';
import api from '../services/api';
import { Invoice, Contractor, MonthlyBill, Project } from '../types';
import { useAuth } from '../contexts/AuthContext';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format } from 'date-fns';

const Invoices: React.FC = () => {
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [contractors, setContractors] = useState<Contractor[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [monthlyBills, setMonthlyBills] = useState<MonthlyBill[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [viewOpen, setViewOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState<Invoice | null>(null);
  const [rejectDialogOpen, setRejectDialogOpen] = useState(false);
  const [rejectingInvoiceId, setRejectingInvoiceId] = useState<string | null>(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const [generateBillOpen, setGenerateBillOpen] = useState(false);
  const [generatingBill, setGeneratingBill] = useState(false);
  const [billFormData, setBillFormData] = useState({
    projectId: '',
    month: new Date().getMonth() + 1,
    year: new Date().getFullYear(),
  });
  const [formData, setFormData] = useState({
    contractorId: '',
    startDate: new Date(),
    endDate: new Date(),
    tax: '0',
    notes: '',
  });
  const { user } = useAuth();

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const [invoicesRes, contractorsRes, projectsRes, billsRes] = await Promise.all([
        api.get('/invoices'),
        api.get('/contractors'),
        api.get('/projects'),
        api.get('/monthly-bills'),
      ]);

      setInvoices(invoicesRes.data);
      setContractors(contractorsRes.data);
      setProjects(projectsRes.data);
      setMonthlyBills(billsRes.data);
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleOpen = () => {
    setFormData({
      contractorId: '',
      startDate: new Date(new Date().getFullYear(), new Date().getMonth(), 1),
      endDate: new Date(),
      tax: '0',
      notes: '',
    });
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleView = async (invoice: Invoice) => {
    try {
      const response = await api.get(`/invoices/${invoice.id}`);
      setSelectedInvoice(response.data);
      setViewOpen(true);
    } catch (error) {
      console.error('Failed to fetch invoice details:', error);
    }
  };

  const handleGenerate = async () => {
    try {
      const data = {
        ...formData,
        startDate: formData.startDate.toISOString(),
        endDate: formData.endDate.toISOString(),
        tax: parseFloat(formData.tax) || 0,
      };

      await api.post('/invoices/generate', data);
      fetchData();
      handleClose();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to generate invoice');
    }
  };

  const exportToExcel = async () => {
    try {
      const response = await api.get('/export/invoices', {
        responseType: 'blob',
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `invoices-${new Date().toISOString().split('T')[0]}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      console.error('Export failed:', error);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'DRAFT':
        return 'warning';
      case 'SUBMITTED':
        return 'info';
      case 'CERTIFIED':
        return 'success';
      case 'APPROVED':
        return 'success';
      case 'REJECTED':
        return 'error';
      case 'PENDING':
        return 'warning';
      case 'PAID':
        return 'success';
      default:
        return 'default';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  const handleGenerateBill = async () => {
    try {
      setGeneratingBill(true);
      await api.post('/monthly-bills/generate', billFormData);
      alert('Monthly project bill generated successfully!');
      setGenerateBillOpen(false);
      setBillFormData({
        projectId: '',
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
      });
      fetchData();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to generate monthly bill');
    } finally {
      setGeneratingBill(false);
    }
  };

  const handleDownloadBill = async (billId: string, fileName: string) => {
    try {
      const response = await api.get(`/monthly-bills/${billId}/download`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to download bill');
    }
  };

  const handleDownloadBillPDF = async (billId: string, fileName: string) => {
    try {
      const response = await api.get(`/monthly-bills/${billId}/download-pdf`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error: any) {
      if (error.response?.status === 404) {
        alert('PDF file not found. This bill may have been generated before PDF support was added. Please regenerate the bill to include PDF.');
      } else {
        alert(error.response?.data?.message || 'Failed to download PDF');
      }
    }
  };

  const isAdmin = user?.role === 'ADMIN';

  return (
    <Box>
      {/* Invoices Section */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Invoices</Typography>
        <Box>
          <Button variant="outlined" onClick={exportToExcel} sx={{ mr: 1 }}>
            Export to Excel
          </Button>
          {isAdmin && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={handleOpen}>
              Generate Invoice
            </Button>
          )}
        </Box>
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Invoice Number</TableCell>
              <TableCell>Contractor</TableCell>
              <TableCell>Period</TableCell>
              <TableCell align="right">Total</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Submission Date</TableCell>
              <TableCell>Payment Date</TableCell>
              {isAdmin && <TableCell>Attachments</TableCell>}
              {isAdmin && <TableCell align="right">Actions</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {invoices.map((invoice) => (
              <TableRow key={invoice.id}>
                <TableCell>{invoice.invoiceNumber}</TableCell>
                <TableCell>{invoice.contractor?.name || '-'}</TableCell>
                <TableCell>
                  {format(new Date(invoice.startDate), 'MMM dd')} - {format(new Date(invoice.endDate), 'MMM dd, yyyy')}
                </TableCell>
                <TableCell align="right">${invoice.total.toFixed(2)}</TableCell>
                <TableCell>
                  <Chip label={invoice.status} color={getStatusColor(invoice.status) as any} size="small" />
                </TableCell>
                <TableCell>
                  {invoice.submissionDate ? format(new Date(invoice.submissionDate), 'MMM dd, yyyy') : '-'}
                </TableCell>
                <TableCell>
                  {invoice.paymentDate ? format(new Date(invoice.paymentDate), 'MMM dd, yyyy') : '-'}
                </TableCell>
                {isAdmin && (
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      {invoice.hasPdfBill && (
                        <Tooltip title={`PDF Bill: ${invoice.pdfBillFileName || 'Uploaded'}`}>
                          <PictureAsPdfIcon color="error" fontSize="small" />
                        </Tooltip>
                      )}
                      {invoice.hasExcelHours && (
                        <Tooltip title={`Excel Hours: ${invoice.excelHoursFileName || 'Uploaded'}`}>
                          <TableChartIcon color="success" fontSize="small" />
                        </Tooltip>
                      )}
                      {!invoice.hasPdfBill && !invoice.hasExcelHours && (
                        <Typography variant="caption" color="text.secondary">-</Typography>
                      )}
                    </Box>
                  </TableCell>
                )}
                {isAdmin && (
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                      {invoice.hasPdfBill && (
                        <Tooltip title="Download PDF Bill">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={async () => {
                              try {
                                const response = await api.get(`/invoices/${invoice.id}/download-pdf`, {
                                  responseType: 'blob',
                                });
                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', invoice.pdfBillFileName || `invoice_${invoice.invoiceNumber}_bill.pdf`);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                              } catch (error: any) {
                                alert(error.response?.data?.message || 'Failed to download PDF bill');
                              }
                            }}
                          >
                            <PictureAsPdfIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      {invoice.hasExcelHours && (
                        <Tooltip title="Download Excel Hours">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={async () => {
                              try {
                                const response = await api.get(`/invoices/${invoice.id}/download-excel`, {
                                  responseType: 'blob',
                                });
                                const url = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement('a');
                                link.href = url;
                                link.setAttribute('download', invoice.excelHoursFileName || `invoice_${invoice.invoiceNumber}_hours.xlsx`);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                              } catch (error: any) {
                                alert(error.response?.data?.message || 'Failed to download Excel file');
                              }
                            }}
                          >
                            <TableChartIcon />
                          </IconButton>
                        </Tooltip>
                      )}
                      <Tooltip title="View Details">
                        <IconButton size="small" onClick={() => handleView(invoice)}>
                          <VisibilityIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    {invoice.status === 'DRAFT' && (
                      <>
                        <Tooltip title="Approve Invoice">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={async () => {
                              if (window.confirm('Approve this draft invoice? Time entries will be marked as BILLED.')) {
                                try {
                                  await api.post(`/invoices/${invoice.id}/approve`);
                                  alert('Invoice approved successfully');
                                  fetchData();
                                } catch (error: any) {
                                  alert(error.response?.data?.message || 'Failed to approve invoice');
                                }
                              }
                            }}
                          >
                            <CheckCircleIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Reject Invoice">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => {
                              setRejectingInvoiceId(invoice.id);
                              setRejectionReason('');
                              setRejectDialogOpen(true);
                            }}
                          >
                            <CancelIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {invoice.status === 'APPROVED' && (
                      <>
                        <Tooltip title="Mark as Paid">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={async () => {
                              if (window.confirm('Mark this invoice as paid? This will set the payment date to today.')) {
                                try {
                                  await api.post(`/invoices/${invoice.id}/mark-paid`);
                                  alert('Invoice marked as paid successfully');
                                  fetchData();
                                } catch (error: any) {
                                  alert(error.response?.data?.message || 'Failed to mark invoice as paid');
                                }
                              }
                            }}
                          >
                            <PaymentIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Mark as Pending">
                          <IconButton
                            size="small"
                            color="warning"
                            onClick={async () => {
                              if (window.confirm('Mark this invoice as pending payment?')) {
                                try {
                                  await api.post(`/invoices/${invoice.id}/mark-pending`);
                                  alert('Invoice marked as pending successfully');
                                  fetchData();
                                } catch (error: any) {
                                  alert(error.response?.data?.message || 'Failed to mark invoice as pending');
                                }
                              }
                            }}
                          >
                            <PendingIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                    {invoice.status === 'PENDING' && (
                      <>
                        <Tooltip title="Mark as Paid">
                          <IconButton
                            size="small"
                            color="success"
                            onClick={async () => {
                              if (window.confirm('Mark this invoice as paid? This will set the payment date to today.')) {
                                try {
                                  await api.post(`/invoices/${invoice.id}/mark-paid`);
                                  alert('Invoice marked as paid successfully');
                                  fetchData();
                                } catch (error: any) {
                                  alert(error.response?.data?.message || 'Failed to mark invoice as paid');
                                }
                              }
                            }}
                          >
                            <PaymentIcon />
                          </IconButton>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Invoice</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <FormControl fullWidth>
              <InputLabel>Contractor</InputLabel>
              <Select
                value={formData.contractorId}
                label="Contractor"
                onChange={(e) => setFormData({ ...formData, contractorId: e.target.value })}
                required
              >
                {contractors.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <LocalizationProvider dateAdapter={AdapterDateFns}>
              <DatePicker
                label="Start Date"
                value={formData.startDate}
                onChange={(date) => setFormData({ ...formData, startDate: date || new Date() })}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
              <DatePicker
                label="End Date"
                value={formData.endDate}
                onChange={(date) => setFormData({ ...formData, endDate: date || new Date() })}
                slotProps={{ textField: { fullWidth: true, required: true } }}
              />
            </LocalizationProvider>
            <TextField
              label="Tax Amount"
              type="number"
              value={formData.tax}
              onChange={(e) => setFormData({ ...formData, tax: e.target.value })}
              fullWidth
              inputProps={{ step: '0.01', min: '0' }}
            />
            <TextField
              label="Notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              fullWidth
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose}>Cancel</Button>
          <Button onClick={handleGenerate} variant="contained">
            Generate
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={viewOpen} onClose={() => setViewOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Invoice Details - {selectedInvoice?.invoiceNumber}</DialogTitle>
        <DialogContent>
          {selectedInvoice && (
            <Box>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">Contractor</Typography>
                <Typography variant="body1">{selectedInvoice.contractor?.name}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">Period</Typography>
                <Typography variant="body1">
                  {format(new Date(selectedInvoice.startDate), 'MMM dd, yyyy')} - {format(new Date(selectedInvoice.endDate), 'MMM dd, yyyy')}
                </Typography>
              </Box>
              {/* File Attachments Section */}
              {(selectedInvoice.hasPdfBill || selectedInvoice.hasExcelHours) && (
                <Box mb={2}>
                  <Typography variant="h6" gutterBottom>Attached Files</Typography>
                  <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                    {selectedInvoice.hasPdfBill && (
                      <Button
                        variant="outlined"
                        startIcon={<PictureAsPdfIcon />}
                        endIcon={<DownloadIcon />}
                        onClick={async () => {
                          try {
                            const response = await api.get(`/invoices/${selectedInvoice.id}/download-pdf`, {
                              responseType: 'blob',
                            });
                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', selectedInvoice.pdfBillFileName || `invoice_${selectedInvoice.invoiceNumber}_bill.pdf`);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                          } catch (error: any) {
                            alert(error.response?.data?.message || 'Failed to download PDF bill');
                          }
                        }}
                      >
                        Download PDF Bill
                        {selectedInvoice.pdfBillFileName && (
                          <Typography variant="caption" sx={{ ml: 1 }}>
                            ({selectedInvoice.pdfBillFileName})
                          </Typography>
                        )}
                      </Button>
                    )}
                    {selectedInvoice.hasExcelHours && (
                      <Button
                        variant="outlined"
                        startIcon={<TableChartIcon />}
                        endIcon={<DownloadIcon />}
                        onClick={async () => {
                          try {
                            const response = await api.get(`/invoices/${selectedInvoice.id}/download-excel`, {
                              responseType: 'blob',
                            });
                            const url = window.URL.createObjectURL(new Blob([response.data]));
                            const link = document.createElement('a');
                            link.href = url;
                            link.setAttribute('download', selectedInvoice.excelHoursFileName || `invoice_${selectedInvoice.invoiceNumber}_hours.xlsx`);
                            document.body.appendChild(link);
                            link.click();
                            link.remove();
                          } catch (error: any) {
                            alert(error.response?.data?.message || 'Failed to download Excel file');
                          }
                        }}
                      >
                        Download Excel Hours
                        {selectedInvoice.excelHoursFileName && (
                          <Typography variant="caption" sx={{ ml: 1 }}>
                            ({selectedInvoice.excelHoursFileName})
                          </Typography>
                        )}
                      </Button>
                    )}
                  </Box>
                </Box>
              )}

              {selectedInvoice.timeEntries && selectedInvoice.timeEntries.length > 0 && (
                <Box mb={2}>
                  <Typography variant="h6" gutterBottom>Time Entries</Typography>
                  <TableContainer>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell>Date</TableCell>
                          <TableCell>Project</TableCell>
                          <TableCell align="right">Hours</TableCell>
                          <TableCell>Description</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedInvoice.timeEntries.map((entry) => (
                          <TableRow key={entry.id}>
                            <TableCell>{format(new Date(entry.dateWorked), 'MMM dd, yyyy')}</TableCell>
                            <TableCell>{entry.project?.name || '-'}</TableCell>
                            <TableCell align="right">{entry.hoursWorked.toFixed(2)}</TableCell>
                            <TableCell>{entry.description}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">Subtotal</Typography>
                <Typography variant="body1">${selectedInvoice.subtotal.toFixed(2)}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="body2" color="text.secondary">Tax</Typography>
                <Typography variant="body1">${(selectedInvoice.tax || 0).toFixed(2)}</Typography>
              </Box>
              <Box mb={2}>
                <Typography variant="h6">Total: ${selectedInvoice.total.toFixed(2)}</Typography>
              </Box>
              {selectedInvoice.payments && selectedInvoice.payments.length > 0 && (
                <Box mb={2}>
                  <Typography variant="h6" gutterBottom>Payments</Typography>
                  {selectedInvoice.payments.map((payment) => (
                    <Box key={payment.id} mb={1}>
                      <Typography variant="body2">
                        ${payment.amount.toFixed(2)} on {format(new Date(payment.paymentDate), 'MMM dd, yyyy')}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          {selectedInvoice?.status === 'DRAFT' && isAdmin && (
            <>
              <Button
                onClick={async () => {
                  if (window.confirm('Approve this draft invoice? Time entries will be marked as BILLED.')) {
                    try {
                      await api.post(`/invoices/${selectedInvoice.id}/approve`);
                      alert('Invoice approved successfully');
                      setViewOpen(false);
                      fetchData();
                    } catch (error: any) {
                      alert(error.response?.data?.message || 'Failed to approve invoice');
                    }
                  }
                }}
                variant="contained"
                color="success"
                startIcon={<CheckCircleIcon />}
              >
                Approve
              </Button>
              <Button
                onClick={() => {
                  setRejectingInvoiceId(selectedInvoice.id);
                  setRejectionReason('');
                  setRejectDialogOpen(true);
                }}
                variant="outlined"
                color="error"
                startIcon={<CancelIcon />}
              >
                Reject
              </Button>
            </>
          )}
          {(selectedInvoice?.status === 'APPROVED' || selectedInvoice?.status === 'PENDING') && isAdmin && (
            <>
              <Button
                onClick={async () => {
                  if (window.confirm('Mark this invoice as paid? This will set the payment date to today.')) {
                    try {
                      await api.post(`/invoices/${selectedInvoice.id}/mark-paid`);
                      alert('Invoice marked as paid successfully');
                      setViewOpen(false);
                      fetchData();
                    } catch (error: any) {
                      alert(error.response?.data?.message || 'Failed to mark invoice as paid');
                    }
                  }
                }}
                variant="contained"
                color="success"
                startIcon={<PaymentIcon />}
              >
                Mark as Paid
              </Button>
              {selectedInvoice?.status === 'APPROVED' && (
                <Button
                  onClick={async () => {
                    if (window.confirm('Mark this invoice as pending payment?')) {
                      try {
                        await api.post(`/invoices/${selectedInvoice.id}/mark-pending`);
                        alert('Invoice marked as pending successfully');
                        setViewOpen(false);
                        fetchData();
                      } catch (error: any) {
                        alert(error.response?.data?.message || 'Failed to mark invoice as pending');
                      }
                    }
                  }}
                  variant="outlined"
                  color="warning"
                  startIcon={<PendingIcon />}
                >
                  Mark as Pending
                </Button>
              )}
            </>
          )}
          <Button onClick={() => setViewOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Reject Invoice Dialog */}
      <Dialog open={rejectDialogOpen} onClose={() => setRejectDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Reject Draft Invoice</DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            Rejecting this invoice will unlink the time entries and make them available for regeneration.
          </Alert>
          <TextField
            label="Rejection Reason *"
            value={rejectionReason}
            onChange={(e) => setRejectionReason(e.target.value)}
            fullWidth
            multiline
            rows={4}
            required
            sx={{ mt: 2 }}
            placeholder="Please provide a reason for rejection so the contractor can fix the issue..."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={async () => {
              if (!rejectionReason.trim()) {
                alert('Please provide a rejection reason');
                return;
              }
              try {
                await api.post(`/invoices/${rejectingInvoiceId}/reject`, {
                  rejectionReason: rejectionReason.trim(),
                });
                alert('Invoice rejected. Contractor can now regenerate it.');
                setRejectDialogOpen(false);
                setRejectingInvoiceId(null);
                setRejectionReason('');
                fetchData();
                if (viewOpen) setViewOpen(false);
              } catch (error: any) {
                alert(error.response?.data?.message || 'Failed to reject invoice');
              }
            }}
            variant="contained"
            color="error"
            disabled={!rejectionReason.trim()}
          >
            Reject Invoice
          </Button>
        </DialogActions>
      </Dialog>

      {/* Monthly Bills Section - Visually Separated */}
      <Box sx={{ mt: 6, pt: 4, borderTop: '2px solid #e0e0e0' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box>
            <Typography variant="h4" gutterBottom>
              Monthly Bills Repository
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Generate and manage monthly Excel bills for contractors by project
            </Typography>
          </Box>
          {isAdmin && (
            <Button 
              variant="contained" 
              color="primary"
              startIcon={<AddIcon />} 
              onClick={() => setGenerateBillOpen(true)}
            >
              Generate Monthly Bill
            </Button>
          )}
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Bill Number</TableCell>
                <TableCell>Type</TableCell>
                <TableCell>Contractor</TableCell>
                <TableCell>Position</TableCell>
                <TableCell>Project</TableCell>
                <TableCell>Client</TableCell>
                <TableCell>Period</TableCell>
                <TableCell align="right">Total Hours</TableCell>
                <TableCell align="right">Total Amount</TableCell>
                <TableCell>Generated</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {monthlyBills.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">
                    <Typography variant="body2" color="text.secondary" sx={{ py: 3 }}>
                      No monthly bills generated yet. Click "Generate Monthly Bill" to create one.
                    </Typography>
                  </TableCell>
                </TableRow>
              ) : (
                monthlyBills.map((bill) => (
                  <TableRow key={bill.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="bold">
                        {bill.billNumber}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip 
                        label={bill.billType === 'PROJECT' ? 'Project' : 'Contractor'} 
                        size="small" 
                        color={bill.billType === 'PROJECT' ? 'primary' : 'default'}
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell>
                      {bill.contractor ? bill.contractor.name : (
                        <Typography variant="body2" color="text.secondary" fontStyle="italic">
                          All Contractors
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      {bill.contractor?.position ? (
                        <Chip 
                          label={bill.contractor.position} 
                          size="small" 
                          variant="outlined"
                        />
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell>{bill.project.name}</TableCell>
                    <TableCell>{bill.project.clientName || '-'}</TableCell>
                    <TableCell>
                      {new Date(bill.year, bill.month - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}
                    </TableCell>
                    <TableCell align="right">{bill.totalHours.toFixed(2)}</TableCell>
                    <TableCell align="right">${bill.totalAmount.toFixed(2)}</TableCell>
                    <TableCell>
                      {format(new Date(bill.createdAt), 'MMM dd, yyyy')}
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        <Tooltip title="Download PDF Bill (with narrative and certification)">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDownloadBillPDF(bill.id, bill.pdfFileName || `${bill.billNumber}.pdf`)}
                          >
                            <PictureAsPdfIcon />
                          </IconButton>
                        </Tooltip>
                        <Tooltip title="Download Excel Bill">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleDownloadBill(bill.id, bill.fileName)}
                          >
                            <TableChartIcon />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>

      {/* Generate Monthly Bill Dialog */}
      <Dialog open={generateBillOpen} onClose={() => setGenerateBillOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Generate Monthly Excel Bill</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Alert severity="info">
              This will generate a monthly Excel bill using only APPROVED time entries for the selected project and month. All contractors working on this project will be included.
            </Alert>
            <FormControl fullWidth required>
              <InputLabel>Project *</InputLabel>
              <Select
                value={billFormData.projectId}
                label="Project *"
                onChange={(e) => setBillFormData({ ...billFormData, projectId: e.target.value })}
              >
                {projects.filter(p => p.isActive).map((p) => (
                  <MenuItem key={p.id} value={p.id}>
                    {p.name} {p.clientName ? `- ${p.clientName}` : ''}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth required>
              <InputLabel>Billing Month *</InputLabel>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Select
                  value={billFormData.month}
                  label="Month"
                  onChange={(e) => setBillFormData({ ...billFormData, month: Number(e.target.value) })}
                  sx={{ flex: 1 }}
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <MenuItem key={m} value={m}>
                      {new Date(2000, m - 1).toLocaleString('default', { month: 'long' })}
                    </MenuItem>
                  ))}
                </Select>
                <Select
                  value={billFormData.year}
                  label="Year"
                  onChange={(e) => setBillFormData({ ...billFormData, year: Number(e.target.value) })}
                  sx={{ flex: 1 }}
                >
                  {Array.from({ length: 5 }, (_, i) => new Date().getFullYear() - 2 + i).map((y) => (
                    <MenuItem key={y} value={y}>
                      {y}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                {billFormData.month && billFormData.year && 
                  `${new Date(billFormData.year, billFormData.month - 1).toLocaleString('default', { month: 'long', year: 'numeric' })}`
                }
              </Typography>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenerateBillOpen(false)}>Cancel</Button>
          <Button
            onClick={handleGenerateBill}
            variant="contained"
            disabled={generatingBill || !billFormData.projectId}
          >
            {generatingBill ? 'Generating...' : 'Generate Bill'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Invoices;

